import React, { useRef } from 'react'
import ModalContainer from '../modals/ModalContainer'
import type { MerchantProductSelectionModalProps } from './types'

const MerchantProductSelectionModal: React.FC<MerchantProductSelectionModalProps> = ({
  isOpen,
  onClose,
  onCreateNew,
  onOpenBrowser,
  mode,
  className = ''
}) => {
  // Prevent double execution in React StrictMode
  const clickProcessingRef = useRef(false);


  if (!isOpen) return null

  const isLinkMode = mode === 'link'
  const modalTitle = isLinkMode ? 'Link to Merchant Product' : 'Add New Product'
  const modalDescription = isLinkMode
    ? 'Select a merchant product to generate Master SKU and create product link.'
    : 'Choose how you\'d like to create your new product listing. You can start fresh or save time by copying from an existing product.'

  // Footer content with help link and cancel button
  const footerContent = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      <a 
        href="#" 
        className="tw-text-sm tw-text-gray-500 hover:tw-text-blue-600 tw-transition-colors tw-flex tw-items-center tw-gap-1"
        onClick={(e) => e.preventDefault()}
      >
        <span className="material-symbols-outlined tw-text-sm">help</span>
        Need Help?
      </a>
      <button
        onClick={onClose}
        className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
      >
        Cancel
      </button>
    </div>
  )

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle={modalDescription}
      size="lg"
      autoHeight={true}
      footer={footerContent}
      className={`tw-w-full tw-max-w-md md:tw-max-w-2xl lg:tw-max-w-4xl ${className}`}
    >


      {/* Action Panels Content */}
      <div className="tw-p-4 md:tw-p-6">
        <div className={`tw-grid tw-grid-cols-1 ${isLinkMode ? '' : 'lg:tw-grid-cols-2'} tw-gap-4 md:tw-gap-6 ${isLinkMode ? 'tw-justify-center' : ''}`}>
          
          {/* Panel 1: Create New Merchant Product - Only show in copy mode */}
          {!isLinkMode && (
            <div className="tw-group" data-testid="start-fresh-option">
              <div
                className="tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-xl tw-p-6 tw-text-center hover:tw-border-blue-500 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-cursor-pointer"
                onClick={(e) => {
                  console.log("🎯 Direct onClick for Start Fresh");
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Prevent double execution in React StrictMode
                  if (clickProcessingRef.current) {
                    console.log("⚠️ Preventing double execution (React StrictMode)");
                    return;
                  }
                  clickProcessingRef.current = true;
                  
                  setTimeout(() => {
                    clickProcessingRef.current = false;
                  }, 100);
                  
                  onCreateNew();
                  onClose();
                }}
              >
                <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-bg-blue-100 tw-rounded-full tw-mb-4 group-hover:tw-bg-blue-200 tw-transition-colors">
                  <span className="material-symbols-outlined tw-text-2xl tw-text-blue-600">add_circle</span>
                </div>
                <h4 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                  Start Fresh
                </h4>
                <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                  Create a brand new product listing for this marketplace store with no pre-existing data. Build a unique product from scratch for your shop.
                </p>
                
                {/* Benefits List */}
                <div className="tw-space-y-2 tw-text-xs tw-text-gray-500 tw-mb-4">
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                    <span>Define all product details for your store</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                    <span>Perfect for unique marketplace offerings</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                    <span>Full control over pricing and description</span>
                  </div>
                </div>
                
                <div className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors hover:tw-bg-blue-700 tw-inline-block">
                  Create New Product
                </div>
              </div>
            </div>
          )}

          {/* Panel 2: Select Existing Merchant Product */}
          <div className={`tw-group ${isLinkMode ? 'tw-max-w-md tw-mx-auto' : ''}`} data-testid="copy-from-merchant-option">
            <div
              className="tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-xl tw-p-6 tw-text-center hover:tw-border-green-500 hover:tw-bg-green-50 tw-transition-all tw-duration-200 tw-cursor-pointer"
              onClick={(e) => {
                console.log("🎯 Direct onClick for Browse Products");
                e.preventDefault();
                e.stopPropagation();
                
                // Prevent double execution in React StrictMode
                if (clickProcessingRef.current) {
                  console.log("⚠️ Preventing double execution (React StrictMode)");
                  return;
                }
                clickProcessingRef.current = true;
                
                setTimeout(() => {
                  clickProcessingRef.current = false;
                }, 100);
                
                onOpenBrowser();
                onClose();
              }}
            >
              <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-bg-green-100 tw-rounded-full tw-mb-4 group-hover:tw-bg-green-200 tw-transition-colors">
                <span className="material-symbols-outlined tw-text-2xl tw-text-green-600">
                  {isLinkMode ? 'link' : 'inventory_2'}
                </span>
              </div>
              <h4 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                {isLinkMode ? 'Link to Merchant Product' : 'Copy & Customize'}
              </h4>
              <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                {isLinkMode 
                  ? 'Connect to an existing merchant product to automatically generate Master SKU and establish product linkage for inventory synchronization.'
                  : 'Use an existing merchant product as a template and modify it to create a new variant. Efficient for similar products with small differences.'
                }
              </p>
              
              {/* Benefits List */}
              <div className="tw-space-y-2 tw-text-xs tw-text-gray-500 tw-mb-4">
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                  <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                  <span>{isLinkMode ? 'Auto-generate Master SKU' : 'Inherit existing product attributes'}</span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                  <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                  <span>{isLinkMode ? 'Maintain product relationship' : 'Quick setup for product variants'}</span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                  <span className="material-symbols-outlined tw-text-sm tw-text-green-600">check</span>
                  <span>{isLinkMode ? 'Sync inventory automatically' : 'Save time on common properties'}</span>
                </div>
              </div>
              
              <div className="tw-bg-green-600 tw-text-white tw-px-6 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors hover:tw-bg-green-700 tw-inline-block">
                {isLinkMode ? 'Browse & Link' : 'Browse Products'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalContainer>
  )
}

export default MerchantProductSelectionModal